import * as React from 'react';
import { useAuth } from 'lib/context';
import { SuperAdminRole } from 'lib/const/SuperAdminRole';
import { FlexForm, UserFormProps } from '../../components/AdminFormComponents';
import { Divider } from '../../index.styled';
import { CustomerSection } from './userDetails/CustomerSection';
import { PersonalInfoSection } from './userDetails/PersonalInfoSection';
import { PasswordSection } from './userDetails/PasswordSection';
import { ContactSection } from './userDetails/ContactSection';
import { CovideoSection } from './userDetails/CovideoSection';
import { JobDescriptionSection } from './userDetails/JobDescriptionSection';
import { AddonSection } from './userDetails/AddonSection';
import { DefaultSection } from './userDetails/DefaultSection';
import { getCustomer } from 'lib/api/superAdminApi';
import { AccessRole, RESELLER_IDS } from 'lib/const';
import { AutomotiveRole } from 'lib/const/AutomotiveRole';
import { LoginAsSection } from './userDetails/LoginAsSection';
import { FastPasswordSection } from './userDetails/FastPasswordSection';
import { CodirectUserSection } from './userDetails/CodirectUserSection';
import { AIUserFeaturesSection } from './userDetails/AIUserFeaturesSection';

export const UserDetailsForm = (
  props: UserFormProps & {
    onSave: (reloadUserOnSave?: boolean) => Promise<boolean>;
    setCurrentResellerId: React.Dispatch<React.SetStateAction<number>>;
  }
) => {
  const [isAIFeaturesSectionVisible, setIsAIFeaturesSectionVisible] =
    React.useState(false);
  const [isAISpotlightEnabledForCustomer, setIsAISpotlightEnabledForCustomer] =
    React.useState(false);
  const [isAIAssistantEnabledForCustomer, setIsAIAssistantEnabledForCustomer] =
    React.useState(false);
  const { isNewUser, setFieldValue, values, onSave, setCustomerName } = props;
  const [isVerifiedDisabled, setIsVerifiedDisabled] = React.useState(false);
  const [isAutomotive, setIsAutomotive] = React.useState(false);
  const [hasCodirectField, setHasCodirectField] = React.useState(false);
  const { userData } = useAuth();

  const showCustomerSection =
    userData.superAdminRole === SuperAdminRole.COVIDEO_SUPER_ADMIN ||
    userData.superAdminRole === SuperAdminRole.NON_COVIDEO_SUPER_ADMIN ||
    userData.superAdminRole === SuperAdminRole.COVIDEO_ADMIN;
  const showOtherSections =
    userData.superAdminRole !== SuperAdminRole.ADMIN &&
    userData.superAdminRole !== SuperAdminRole.ACCESS;

  const loadCustomer = async () => {
    const customerDetails = await getCustomer(values.customerId);
    if (customerDetails) {
      if (isNewUser) {
        const verified = customerDetails.userCount < customerDetails.maxUsers;
        const stringifiedAutomotiveRole = values.automotiveRole?.toString();
        setIsVerifiedDisabled(!verified);
        setFieldValue(`verified`, verified);
        setFieldValue(
          `automotiveRole`,
          stringifiedAutomotiveRole
            ? stringifiedAutomotiveRole
            : values.access === Number(AccessRole.ADMIN)
              ? AutomotiveRole.SALES_MANAGER
              : AutomotiveRole.SALESPERSON
        );
      }
      setIsAutomotive(!!customerDetails.versionCDSEnabled);
    }

    return customerDetails;
  };

  React.useEffect(() => {
    if (values.customerId > 0) {
      loadCustomer().then(customerDetails => {
        if (customerDetails) {
          setCustomerName && setCustomerName(customerDetails.business);
          setFieldValue('resellerId', customerDetails.resellerId);
          setHasCodirectField(!!customerDetails.codirectId);
          setIsAISpotlightEnabledForCustomer(
            !!customerDetails.spotlightEnabled
          );
          setIsAIAssistantEnabledForCustomer(
            !!customerDetails.quickVideoAvatarEnabled
          );
          setIsAIFeaturesSectionVisible(
            (customerDetails.spotlightEnabled ||
              customerDetails.quickVideoAvatarEnabled) &&
              userData.superAdminRole === SuperAdminRole.COVIDEO_SUPER_ADMIN
          );
        }
      });
    }
  }, [values.customerId]);

  const isCurrentCDKReseller =
    values.resellerId?.toString() === RESELLER_IDS.CDK;

  // show fast password only when editing existing users
  const showFastPasswordSection = !isNewUser && values.id;

  // show login as only when editing existing users
  const showLoginAsSection = !isNewUser && values?.id && values?.username;

  return (
    <>
      <FlexForm>
        {showCustomerSection && (
          <>
            <CustomerSection {...props} />
            <Divider />
          </>
        )}
        {showLoginAsSection && (
          <>
            <LoginAsSection
              userId={Number(values.id)}
              username={values.username}
              onSave={onSave}
            />
            <Divider />
          </>
        )}
        <>
          <PersonalInfoSection {...props} />
          <Divider />
        </>

        {showFastPasswordSection ? (
          <>
            <FastPasswordSection userId={values.id as number} />
            <Divider />
          </>
        ) : (
          <>
            <PasswordSection {...props} />
            <Divider />
          </>
        )}
        <>
          <ContactSection {...props} />
          <Divider />
        </>
        {showOtherSections && (
          <>
            <CovideoSection
              {...props}
              isAutomotive={isAutomotive}
              isVerifiedDisabled={isVerifiedDisabled}
            />
            <Divider />
          </>
        )}
        {isAIFeaturesSectionVisible && (
          <>
            <AIUserFeaturesSection
              {...props}
              isAISpotlightEnabledForCustomer={isAISpotlightEnabledForCustomer}
              isAIAssistantEnabledForCustomer={isAIAssistantEnabledForCustomer}
            />
            <Divider />
          </>
        )}
        <>
          <JobDescriptionSection {...props} />
          <Divider />
        </>

        {isNewUser && (
          <>
            <DefaultSection {...props} />
            <Divider />
          </>
        )}
        {showOtherSections && (
          <>
            {!isNewUser && !isCurrentCDKReseller && (
              <>
                <AddonSection {...props} />
                <Divider />
              </>
            )}
          </>
        )}
        {hasCodirectField && (
          <>
            <CodirectUserSection {...props} />
            <Divider />
          </>
        )}
      </FlexForm>
    </>
  );
};
